import { setToken, clearToken, setUserName, addRole, updateRole, deleteRole } from '../stateManagement/authSlice';
import store from '../stateManagement/store';
import { AuthPoolData } from './ApiURL';
import { AuthenticateModel, ConfirmModel, RegisterModel } from "@ontime/types";
import { removeSelectedOrganization } from '../stateManagement/selectedOrganizationSlice';
import { removeSelectedServiceProvider } from '../stateManagement/selectedServiceProviderSlice';
import { removeTarget } from '../stateManagement/targetSlice';
import { removeSelectedOrganizationForUser } from '../stateManagement/selectedOrganizationForUserSlice';
import { removeSelectedServiceProviderForUser } from '../stateManagement/selectedServiceProviderForUserSlice';
import { AuthenticationDetails, CognitoUser, CognitoUserAttribute, CognitoUserPool } from 'amazon-cognito-identity-js';
import { closeClientsDialog } from '../stateManagement/clientsDialogSlice';
import { closeServiceProviderAvailabilitiesDialog } from '../stateManagement/serviceProviderAvailabilitiesDialogSlice';
import { closeOrganizationTasksDialog } from '../stateManagement/organizationTasksDialogSlice';
import { closeLoyaltyPointsDialog } from '../stateManagement/loyaltyPointsDialogSlice';
import { UserRole } from "@ontime/types";

const userPool = new CognitoUserPool(AuthPoolData);

class AuthService {

  async forgotPassword(emailAddress: string): Promise<{ success: boolean }> {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: emailAddress,
        Pool: userPool,
      };
      const cognitoUser = new CognitoUser(userData);

      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve({ success: true });
        },
        onFailure: (err) => {
          reject((err.message || JSON.stringify(err)) ?? 'Password reset request failed, please try again.');
        },
      });
    });
  }

  async confirmPassword(emailAddress: string, code: string, newPassword: string): Promise<{ success: boolean }> {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: emailAddress,
        Pool: userPool,
      };
      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve({ success: true });
        },
        onFailure: (err) => {
          reject((err.message || JSON.stringify(err)) ?? 'Password reset failed, please try again.');
        },
      });
    });
  }

  async resendCode(emailAddress: string): Promise<{ success: boolean }> {
    return new Promise((resolve, reject) => {


      const userData = {
        Username: emailAddress,
        Pool: userPool,
      };
      const cognitoUser = new CognitoUser(userData);
      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject((err.message || JSON.stringify(err)) ?? 'Resending Code failed, please try again.');
          return;
        }
        resolve({ success: true });
        // setMessage('Confirmation code resent successfully.');
      });

    });
  }

  async confirm(input: ConfirmModel): Promise<{ success: boolean }> {
    return new Promise((resolve, reject) => {
      store.dispatch(setUserName(input.emailAddress));

      const userData = {
        Username: input.emailAddress,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmRegistration(input.confirmationCode, true, (err, result) => {

        if (err) {
          reject((err.message || JSON.stringify(err)) ?? 'Confirmation failed, please try again.');
          return;
        }
        console.log('confirm', result)
        resolve({ success: true });
      });
    });
  }

  async register(input: RegisterModel): Promise<{ success: boolean }> {
    return new Promise((resolve, reject) => {
      store.dispatch(setUserName(input.emailAddress));

      const attributeList = [
        new CognitoUserAttribute({
          Name: 'email',
          Value: input.emailAddress,
        }),
      ];

      userPool.signUp(
        input.emailAddress,
        input.password,
        attributeList,
        [],
        (err, result) => {
          if (err) {
            reject((err.message || JSON.stringify(err)) ?? 'Registration failed, please try again.');
            return;
          }
          if (result) {
            resolve({ success: true });
          } else {
            reject(new Error('Unknown registration error.'));
          }
        }
      );
    });
  }

  // When login succeeds, also pull roles from token payload (if backend injects them)
  async login(input: AuthenticateModel): Promise<{ success: boolean }> {
    return new Promise((resolve, reject) => {
      store.dispatch(setUserName(input.emailAddress));

      const authenticationDetails = new AuthenticationDetails({
        Username: input.emailAddress,
        Password: input.password,
      });

      const userData = {
        Username: input.emailAddress,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          const idToken = result.getIdToken().getJwtToken();
          const decoded = JSON.parse(atob(idToken.split('.')[1])); // decode JWT payload

          const roles: UserRole[] = decoded["custom:roles"]
            ? JSON.parse(decoded["custom:roles"])
            : [];

          const expireInSeconds =
            result.getAccessToken().getExpiration() - Math.floor(Date.now() / 1000);
          const currentTime = Math.floor(Date.now() / 1000);
          const expiryTime = currentTime + expireInSeconds;

          store.dispatch(
            setToken({
              idToken,
              expiryTime,
              roles,
            })
          );

          resolve({ success: true });
        },
        onFailure: (err) => {
          reject(
            (err.message || JSON.stringify(err)) ?? 'Login failed. Please try again.'
          );
        },
      });
    });
  }
  logout() {
    store.dispatch(clearToken());
    store.dispatch(removeTarget());
    store.dispatch(removeSelectedOrganization());
    store.dispatch(removeSelectedServiceProvider());
    store.dispatch(removeSelectedOrganizationForUser());
    store.dispatch(removeSelectedServiceProviderForUser());
    store.dispatch(closeClientsDialog());
    store.dispatch(closeOrganizationTasksDialog());
    store.dispatch(closeServiceProviderAvailabilitiesDialog());
    store.dispatch(closeServiceProviderAvailabilitiesDialog());
    store.dispatch(closeLoyaltyPointsDialog());
  }

  getToken() {
    return store.getState().auth.idToken;
  }

  isTokenExpired(): boolean {
    if (!store.getState().auth.expiryTime) {
      return true;
    }
    const currentTime = Math.floor(Date.now() / 1000);

    return (currentTime > (store.getState().auth.expiryTime ?? 0));
  }



  private getRolesFromState(): UserRole[] {
    return store.getState().auth.roles ?? [];
  }

  getRoles(): UserRole[] {
    return this.getRolesFromState();
  }

  /**
   * Helper to get current CognitoUser from localStorage/session
   */
  private getCurrentCognitoUser(): CognitoUser | null {
    const username = store.getState().auth.email;
    if (!username) return null;
    return new CognitoUser({ Username: username, Pool: userPool });
  }

  /**
   * Helper to refresh token after updating attributes
   */
  private async refreshToken(): Promise<{ success: boolean }> {
    return new Promise((resolve, reject) => {
      const user = this.getCurrentCognitoUser();
      if (!user) {
        reject('No user logged in');
        return;
      }
      user.getSession((err: any, session: any) => {
        if (err || !session) {
          reject(err?.message || 'Failed to get session');
          return;
        }
        // Update Redux token with new roles from token
        const idToken = session.getIdToken().getJwtToken();
        const decoded = JSON.parse(atob(idToken.split('.')[1]));
        const roles: UserRole[] = decoded["custom:roles"]
          ? JSON.parse(decoded["custom:roles"])
          : [];
        const expireInSeconds =
          session.getAccessToken().getExpiration() - Math.floor(Date.now() / 1000);
        const currentTime = Math.floor(Date.now() / 1000);
        const expiryTime = currentTime + expireInSeconds;
        store.dispatch(
          setToken({
            idToken,
            expiryTime,
            roles,
          })
        );
        resolve({ success: true });
      });
    });
  }

  /**
   * Add a new role and update Cognito user attribute 'custom:roles'
   */
  async addRole(newRole: UserRole): Promise<{ success: boolean }> {
    try {
      // Get current roles and add newRole
      const roles = this.getRolesFromState();
      const updatedRoles = [...roles, newRole];
      const user = this.getCurrentCognitoUser();
      if (!user) return { success: false };
      await new Promise<void>((resolve, reject) => {
        user.updateAttributes(
          [
            new CognitoUserAttribute({
              Name: 'custom:roles',
              Value: JSON.stringify(updatedRoles),
            }),
          ],
          (err) => {
            if (err) reject(err);
            else resolve();
          }
        );
      });
      await this.refreshToken();
      // Optionally update Redux roles slice for consistency
      store.dispatch(addRole(newRole));
      return { success: true };
    } catch (err) {
      return { success: false };
    }
  }

  /**
   * Update a role and update Cognito user attribute 'custom:roles'
   */
  async updateRole(organizationId: string, updatedRole: string): Promise<{ success: boolean }> {
    try {
      const roles = this.getRolesFromState();
      const updatedRoles = roles.map(r =>
        r.organizationId === organizationId
          ? { ...r, role: updatedRole }
          : r
      );
      const user = this.getCurrentCognitoUser();
      if (!user) return { success: false };
      await new Promise<void>((resolve, reject) => {
        user.updateAttributes(
          [
            new CognitoUserAttribute({
              Name: 'custom:roles',
              Value: JSON.stringify(updatedRoles),
            }),
          ],
          (err) => {
            if (err) reject(err);
            else resolve();
          }
        );
      });
      await this.refreshToken();
      store.dispatch(updateRole({ organizationId, newRole: updatedRole }));
      return { success: true };
    } catch (err) {
      return { success: false };
    }
  }

  /**
   * Delete a role and update Cognito user attribute 'custom:roles'
   */
  async deleteRole(organizationId: string): Promise<{ success: boolean }> {
    try {
      const roles = this.getRolesFromState();
      const updatedRoles = roles.filter(r => r.organizationId !== organizationId);
      const user = this.getCurrentCognitoUser();
      if (!user) return { success: false };
      await new Promise<void>((resolve, reject) => {
        user.updateAttributes(
          [
            new CognitoUserAttribute({
              Name: 'custom:roles',
              Value: JSON.stringify(updatedRoles),
            }),
          ],
          (err) => {
            if (err) reject(err);
            else resolve();
          }
        );
      });
      await this.refreshToken();
      store.dispatch(deleteRole(organizationId));
      return { success: true };
    } catch (err) {
      return { success: false };
    }
  }



}

export default new AuthService();